import React, { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Select, message } from 'antd';
import { ChevronLeft, Pause, X } from 'react-feather';

import styles from './stylesEditRulePage.module.css';
import { SIDER_TABS } from '../../../../../utils/Constants';
import { NAVIGATION_ROUTES } from '../../../../../utils/NavigationUtils';
import Layout from '../../../../CommonComponents/Layout/Layout';
import SummaryPanel from '../SummaryPanel/SummaryPanel';
import {
  useWithdrawalReview,
  WithdrawalReviewProvider,
} from '../../WithdrawalReviewContext';

function EditRulePageContent() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();

  const { handleUpdateRule, handleDeactivateRule, handleDeleteRule } =
    useWithdrawalReview();
  const rule = useMemo(() => location.state?.rule, [location.state]);

  useEffect(() => {
    if (rule) {
      form.setFieldsValue({
        name: rule.name,
        min_transaction_amount: rule.min_transaction_amount,
        max_transaction_amount: rule.max_transaction_amount,
        num_transactions: rule.num_transactions,
        period_type: rule.period_type,
      });
    }
  }, [rule, form]);

  const handleSave = async (values) => {
    values = {
      ...values,
      min_transaction_amount: values.min_transaction_amount || 0,
      max_transaction_amount: values.max_transaction_amount || 0,
      num_transactions: values.num_transactions || 0,
    };
    await handleUpdateRule({ rule_id: rule.rule_id, ...values });
    message.success('Withdrawal Review Rule Updated');
    navigate(NAVIGATION_ROUTES.WITHDRAWAL_REVIEW_RULES);
  };

  const handleCancel = () => {
    navigate(NAVIGATION_ROUTES.WITHDRAWAL_REVIEW_RULES);
  };

  const handleDelete = async () => {
    await handleDeleteRule(rule.rule_id);
    message.success('Withdrawal Review Rule Deleted');
    navigate(NAVIGATION_ROUTES.WITHDRAWAL_REVIEW_RULES);
  };

  const handleSuspend = async () => {
    await handleDeactivateRule(rule.rule_id);
    message.success('Withdrawal Review Rule Suspended');
    navigate(NAVIGATION_ROUTES.WITHDRAWAL_REVIEW_RULES);
  };

  const formValues = Form.useWatch([], form);

  return (
    <Layout name={SIDER_TABS.TRANSACTIONS}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button className={styles.backButton} onClick={handleCancel}>
              <ChevronLeft size={20} />
            </button>
            <h1>Edit Rule</h1>
          </div>
          <div className={styles.buttonContainer}>
            <div className={styles.buttonGroup}>
              <button onClick={handleCancel} className={styles.cancelButton}>
                Cancel
              </button>
              <button
                type="submit"
                form="editRuleForm"
                className={styles.saveButton}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>

        <div className={styles.content}>
          {/* Left Section - Form */}
          <div className={styles.formSection}>
            <Form
              id="editRuleForm"
              form={form}
              layout="vertical"
              onFinish={handleSave}
              initialValues={{
                name: '',
                min_transaction_amount: '',
                max_transaction_amount: '',
                num_transactions: '',
                period_type: '',
              }}
            >
              {/* Name Section */}
              <div className={styles.section}>
                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <span>Name</span>
                    <Form.Item
                      name="name"
                      rules={[{ required: true, message: 'Name is required' }]}
                    >
                      <input
                        type="text"
                        className={styles.inputField}
                        placeholder="Enter rule name"
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>

              <div className={styles.underline}></div>

              {/* Conditions */}
              <div className={styles.section}>
                <h3>Conditions</h3>
                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <span>Min Transaction Amount</span>
                    <Form.Item name="min_transaction_amount">
                      <input
                        type="number"
                        className={styles.inputField}
                        placeholder="e.g. 10"
                      />
                    </Form.Item>
                  </div>
                  <div className={styles.formField}>
                    <span>Max Transaction Amount</span>
                    <Form.Item name="max_transaction_amount">
                      <input
                        type="number"
                        className={styles.inputField}
                        placeholder="e.g. 500"
                      />
                    </Form.Item>
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formField}>
                    <span>Number of Transactions</span>
                    <Form.Item name="num_transactions">
                      <input
                        type="number"
                        className={styles.inputField}
                        placeholder="e.g. 3"
                      />
                    </Form.Item>
                  </div>
                  <div className={styles.formField}>
                    <span>Period</span>
                    <Form.Item
                      name="period_type"
                      rules={[
                        { required: true, message: 'Period is required' },
                      ]}
                    >
                      <Select
                        className={styles.selectField}
                        placeholder="Select period"
                        options={[
                          {
                            value: 'SINGLE_TRANSACTION',
                            label: 'Single Transaction',
                          },
                          { value: 'HOUR', label: 'Hour' },
                          { value: 'DAY', label: 'Day' },
                          { value: 'WEEK', label: 'Week' },
                          { value: 'MONTH', label: 'Month' },
                          { value: 'YEAR', label: 'Year' },
                          { value: 'ALL_TIME', label: 'All Time' },
                        ]}
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>
            </Form>
          </div>

          {/* Right Section - Summary */}
          <div className={styles.summarySection}>
            <SummaryPanel data={formValues} />

            <div className={styles.actionButtons}>
              <button onClick={handleSuspend} className={styles.suspendButton}>
                <Pause size={16} />
                Suspend
              </button>
              <button onClick={handleDelete} className={styles.deleteButton}>
                <X size={16} />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default function EditRulePage() {
  return (
    <WithdrawalReviewProvider>
      <EditRulePageContent />
    </WithdrawalReviewProvider>
  );
}
